import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../utils/toastUtils";
import addressAPI from "../../api/addressApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";
import { MapPin, Plus, Trash2, ChevronDown, ChevronUp, User } from "lucide-react";

const INITIAL_FORM_DATA = {
  firstName:   "",
  lastName:    "",
  addressType: "home",
  addressLine1: "",
  addressLine2: "",
  pinCode:     "",
  district:    "",
  state:       "",
  country:     "",
};

const ADDRESS_TYPES = ["home", "work", "other"];

function Addresses() {
  useAlphabeticInput();
  useNumericInput();

  const [isOpen,    setIsOpen]    = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [formData,  setFormData]  = useState(INITIAL_FORM_DATA);
  const [errors,    setErrors]    = useState({});

  useEffect(() => {
    fetchAddress();
  }, []);

  const fetchAddress = async () => {
    try {
      const res = await addressAPI.getAllAddress();
      if (res.code === 200) setAddresses(res.data);
    } catch (err) {}
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName)    newErrors.firstName    = "Please Enter First Name";
    if (!formData.addressType)  newErrors.addressType  = "Please Enter Address Type";
    if (!formData.addressLine1) newErrors.addressLine1 = "Please Enter Address";
    if (!formData.pinCode) {
      newErrors.pinCode = "Please Enter PinCode";
    } else if (formData.pinCode.length !== 6) {
      newErrors.pinCode = "PinCode must be 6 digits";
    }
    if (!formData.district) newErrors.district = "Please Enter District";
    if (!formData.state)    newErrors.state    = "Please Enter State";
    if (!formData.country)  newErrors.country  = "Please Enter Country";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const res = await addressAPI.addNewAddress(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setTimeout(() => {
          setIsOpen(false);
          fetchAddress();
        }, 1000);
        setFormData(INITIAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const deleteAddress = async (id) => {
    try {
      const res = await addressAPI.deleteAddress(id);
      if (res.code === 200) fetchAddress();
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div>
      <p className="vp-eyebrow">Shipping</p>
      <h2 className="vp-title">Saved Addresses ({addresses.length})</h2>
      <div className="vp-rule" />

      {/* ── Add New Address Accordion ── */}
      <div className="mb-4">
        <div
          className={`vp-accordion-head${isOpen ? " open" : ""}`}
          onClick={() => setIsOpen((v) => !v)}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Plus size={14} style={{ color: "var(--color-accent-main)" }} />
            Add New Address
          </span>
          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>

        {isOpen && (
          <div className="vp-accordion-body">
            {/* Contact */}
            <p className="vp-block-title">Contact Person</p>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="vp-label">First Name *</label>
                <div className="vp-icon-wrap">
                  <User size={13} />
                  <input
                    className="vp-input only-alphabets"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First name"
                  />
                </div>
                {errors.firstName && <p className="vp-error">{errors.firstName}</p>}
              </div>
              <div className="col-md-6">
                <label className="vp-label">Last Name</label>
                <div className="vp-icon-wrap">
                  <User size={13} />
                  <input
                    className="vp-input only-alphabets"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            {/* Address Type */}
            <p className="vp-block-title">Address Type</p>
            <div className="vp-radio-group mb-3">
              {ADDRESS_TYPES.map((type) => (
                <label
                  key={type}
                  className={`vp-radio-label${formData.addressType === type ? " vp-radio-selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="checkshipping"
                    value={type}
                    checked={formData.addressType === type}
                    onChange={() => setFormData((prev) => ({ ...prev, addressType: type }))}
                  />
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </label>
              ))}
            </div>
            {errors.addressType && <p className="vp-error mb-2">{errors.addressType}</p>}

            {/* Address Lines */}
            <p className="vp-block-title">Address Details</p>
            <div className="mb-3">
              <label className="vp-label">Address Line 1 *</label>
              <input
                className="vp-input-plain"
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleChange}
                placeholder="House / Flat / Street"
              />
              {errors.addressLine1 && <p className="vp-error">{errors.addressLine1}</p>}
            </div>
            <div className="mb-3">
              <label className="vp-label">Address Line 2 (Optional)</label>
              <input
                className="vp-input-plain"
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Landmark, Area"
              />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="vp-label">Pin Code *</label>
                <input
                  className="vp-input-plain only-numeric"
                  type="text"
                  maxLength={6}
                  name="pinCode"
                  value={formData.pinCode}
                  onChange={handleChange}
                  placeholder="6 digits"
                />
                {errors.pinCode && <p className="vp-error">{errors.pinCode}</p>}
              </div>
              <div className="col-md-4">
                <label className="vp-label">District *</label>
                <input
                  className="vp-input-plain only-alphabets"
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
                {errors.district && <p className="vp-error">{errors.district}</p>}
              </div>
              <div className="col-md-4">
                <label className="vp-label">State *</label>
                <input
                  className="vp-input-plain only-alphabets"
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && <p className="vp-error">{errors.state}</p>}
              </div>
            </div>

            <div className="mb-4">
              <label className="vp-label">Country *</label>
              <input
                className="vp-input-plain only-alphabets"
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
              {errors.country && <p className="vp-error">{errors.country}</p>}
            </div>

            <button className="vp-btn-primary" onClick={handleSubmit}>
              Save Address
            </button>
          </div>
        )}
      </div>

      {/* ── Address Cards ── */}
      {addresses.length > 0 ? (
        <div className="row g-3">
          {addresses.map((address) => (
            <div key={address._id} className="col-md-6">
              <div className="vp-addr">
                <span className="vp-addr-badge gold">
                  {address.addressType || "Home"}
                </span>
                <p className="vp-addr-name">
                  {address.firstName} {address.lastName}
                </p>
                <p className="vp-addr-text">
                  {address.addressLine1}
                  {address.addressLine2 ? `, ${address.addressLine2}` : ""}
                  <br />
                  {address.district}, {address.state} — {address.pinCode}
                  <br />
                  {address.country}
                </p>
                <button
                  className="vp-btn-ghost"
                  onClick={() => deleteAddress(address._id)}
                >
                  <Trash2 size={10} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="vp-empty">
          <MapPin size={46} className="vp-empty-icon" />
          <p className="vp-empty-title">No addresses saved</p>
          <p className="vp-empty-sub">
            Add a delivery address to get started.
          </p>
        </div>
      )}
    </div>
  );
}

export default Addresses;